import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';

import { getRoundState } from '../components/_organisms/LiveGame';
import { TimesyncContext } from '../providers/TimesyncProvider/TimesyncProvider';
import BackendService from '../services/backendService';
import { Game } from '../services/backendService/types';
import { RoundState } from '../utils/roundUtils';
import { useMetrics } from './useMetrics';
import useRouterMatch from './useRouterMatch';

export const gameCacheKey = 'game';

export const useRawGame = (gameId?: string | null) => {
  const client = useQueryClient();
  const { data, isFetching } = useQuery<Game | undefined>({
    queryKey: [gameCacheKey, gameId],
    queryFn: async _ => {
      if (!gameId) return Promise.resolve(undefined);
      const res = await BackendService.instance.game.retrieve(gameId);
      if (res) {
        const gameSuccesCacheKey = [gameCacheKey, gameId, 'success'];
        const previousGameSuccess = client.getQueryData<Game>(gameSuccesCacheKey);
        if (!previousGameSuccess) {
          client.setQueryData<Game | undefined>(gameSuccesCacheKey, res);
        }
      }
      return res;
    },
    enabled: Boolean(gameId),
    refetchInterval: undefined,
    staleTime: 1000 * 10,
    refetchOnWindowFocus: true,
  });

  return {
    data,
    isFetching,
  };
};

function useGame(gameId?: string | null) {
  const { now } = useContext(TimesyncContext);
  const client = useQueryClient();
  const { templateId } = useRouterMatch();
  const metrics = useMetrics();

  const { data, isFetching } = useRawGame(gameId);

  const [roundState, setRoundState] = useState<RoundState | undefined>(
    getRoundState({ game: data, now }),
  );

  const { mutate: setAllRoundsAsCompleted } = useMutation({
    mutationFn: () => {
      if (!gameId) return Promise.resolve(undefined);
      metrics.logEvent('Game.All.Rounds.Completed', { gameId });
      return BackendService.instance.game.setAllRoundsAsCompleted(gameId);
    },
    onSuccess: latestGame => {
      const cKey = [gameCacheKey, gameId];
      client.cancelQueries({
        queryKey: [gameCacheKey, gameId]
      });
      client.setQueryData<Game | undefined>(cKey, () => {
        return latestGame;
      });
    },
  });
  const { mutate: editTitle } = useMutation({
    mutationFn: ({ title, gameId }: { title: string; gameId: string }) => {
      const gameIdToUse = gameId || gameId;
      if (!gameIdToUse) return Promise.resolve(undefined);
      metrics.logEvent('Game.Edit.Title', { gameIdToUse });
      return BackendService.instance.game.editTitle({
        gameId: gameIdToUse,
        title,
      });
    },
    onMutate: async ({ title, gameId }) => {
      const gameIdToUse = gameId || gameId;
      const cKey = [gameCacheKey, gameIdToUse];
      const previousGame = client.getQueryData<Game | undefined>(cKey);
      if (previousGame) {
        client.setQueryData<Game | undefined>(cKey, () => {
          return {
            ...previousGame,
            title,
          };
        });
      }
      const allFetchedGame = client.getQueryData<
        { pages: { items: Game[] }[] } | undefined
      >(['results', templateId]);
      if (allFetchedGame) {
        client.setQueryData<{ pages: { items: Game[] }[] } | undefined>(
          ['results', templateId],
          () => {
            return {
              ...allFetchedGame,
              pages: allFetchedGame.pages.map(page => {
                return {
                  ...page,
                  items: page.items.map(game => {
                    if (game.id === gameIdToUse) {
                      return {
                        ...game,
                        title,
                      };
                    }
                    return game;
                  }),
                };
              }),
            };
          },
        );
      }
    },
  });

  useEffect(() => {
    if (data && !!now) {
      setRoundState(getRoundState({ game: data, now }));
    }
  }, [data, now]);

  return {
    game: data,
    roundState,

    isFetching,
    setRoundState,
    setAllRoundsAsCompleted,
    editTitle,
  };
}

export default useGame;
